import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import API_ENDPOINTS from '../apiConfig';
import Notification from "./Notification";
import { authFetch } from '../utils/authFetch';
import { logout } from '../utils/auth';
import { reportServerVersion } from '../utils/serverVersion';
import { useSite } from '../context/SiteContext';

// Survives a reload while on the OTP step (e.g. mobile F5) — sessionStorage
// rather than localStorage since a pending code belongs to this one login
// attempt/tab, not something that should linger past closing it.
const OTP_STORAGE_KEY = 'ezentry_pending_otp';
// Mirrors AuthService::requestOtp()'s otp_expires window (INTERVAL 10 MINUTE) —
// if the stored attempt is older than this, don't bother restoring it; the
// backend will already reject it as expired anyway.
const OTP_VALIDITY_MS = 10 * 60 * 1000;

function readPendingOtp() {
  try {
    const raw = sessionStorage.getItem(OTP_STORAGE_KEY);
    if (!raw) return null;
    const { email, requestedAt } = JSON.parse(raw);
    if (!email || !requestedAt || Date.now() - requestedAt > OTP_VALIDITY_MS) {
      sessionStorage.removeItem(OTP_STORAGE_KEY);
      return null;
    }
    return { email, requestedAt };
  } catch {
    sessionStorage.removeItem(OTP_STORAGE_KEY);
    return null;
  }
}

function clearPendingOtp() {
  sessionStorage.removeItem(OTP_STORAGE_KEY);
}

function Login({ setUsername }) {
  const { fetchSites } = useSite();
  const { t } = useTranslation();
  const pendingOtp = readPendingOtp();
  const [email, setEmail] = useState(pendingOtp?.email ?? '');
  const [otp, setOtp] = useState(new Array(6).fill("")); // 6 boxes
  const inputsRef = useRef([]);
  const [step, setStep] = useState(pendingOtp ? 2 : 1);
  const [checkingToken, setCheckingToken] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const [blockedMessage, setBlockedMessage] = useState("");

  // ✅ Check token validity on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCheckingToken(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const now = Date.now() / 1000;
      if (decoded.exp > now) {
        clearPendingOtp(); // already have a valid session — any pending OTP entry is moot
        setUsername(decoded.name || decoded.email);
        navigate('/dashboard');
      } else {
        localStorage.removeItem('token');
        setCheckingToken(false);
      }
    } catch (err) {
      localStorage.removeItem('token');
      setCheckingToken(false);
    }
  }, [navigate, setUsername]);

  // Focus the first OTP box whenever step 2 becomes active — covers both the
  // normal "code just sent" path and reload-restoring straight into step 2.
  useEffect(() => {
    if (step !== 2) return;
    const timer = setTimeout(() => inputsRef.current[0]?.focus(), 200);
    return () => clearTimeout(timer);
  }, [step]);

  // Step 1: request code
  const handleRequestCode = async (e) => {
    e.preventDefault();

    // ✅ Immediately go to step 2
    setStep(2);
    setOtp(new Array(6).fill(""));
    sessionStorage.setItem(OTP_STORAGE_KEY, JSON.stringify({ email, requestedAt: Date.now() }));

    try {
      const response = await fetch(API_ENDPOINTS.RequestCode, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      reportServerVersion(response);

      if (!response.ok) {
        throw new Error("Failed to request code");
      }

      // (optional) you could show a small toast here like "Code sent"
    } catch (err) {
      // ⚠️ Stay on step 2, but notify user
      setErrorMessage("Failed to send code. Please check your email.");
    }
  };


  // Step 2: handle OTP typing
  const handleChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1].focus();
    }

    if (newOtp.every((d) => d !== "")) {
      handleVerifyCode(newOtp.join(""));
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").slice(0, 6).split("");
    const newOtp = [...otp];
    pasteData.forEach((char, i) => {
      if (/^[0-9]$/.test(char) && i < 6) {
        newOtp[i] = char;
      }
    });
    setOtp(newOtp);
    if (pasteData.length === 6) {
      handleVerifyCode(newOtp.join(""));
    }
  };

  // Step 2: verify code
  const handleVerifyCode = async (finalCode) => {
    try {
      const verifyData = { email, otp: finalCode };
      const response = await fetch(API_ENDPOINTS.Login, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(verifyData),
      });
      reportServerVersion(response);

      if (!response.ok) {
        if (response.status === 403) {
          setBlockedMessage(
            t("too_much_devices")
          );
          setStep(0); // leave OTP step
          clearPendingOtp();
          return;
        }

        if (response.status === 401) {
          throw new Error(t("Wrong code"));
        }

        throw new Error(t("Verification failed"));
      }


      const data = await response.json();
      localStorage.setItem('token', data.token);
      clearPendingOtp();
      fetchSites();
      /*startTokenRefreshTimer({
        refreshEveryMs: 1000 * API_ENDPOINTS.checkTimerSeconds,
        onFail: () => {
          localStorage.removeItem("token");
          logout(navigate);
          navigate("/");
        },
      });*/

      const decoded = jwtDecode(data.token);
      setUsername(decoded.name || decoded.email);

      navigate('/dashboard');
    } catch (err) {
      setErrorMessage(err.message);
      setOtp(new Array(6).fill(""));
      inputsRef.current[0]?.focus();
    }
  };

  if (checkingToken) {
    return <div className="container mt-5 text-center">{t('Checking session...')}</div>;
  }

  return (
    <div className="login-wrapper">
      <div className="login-box">
        <h2>{t('Login')}</h2>
        {!blockedMessage && step === 1 && (
          <form onSubmit={handleRequestCode}>

            <input
              type="email"
              className="form-control mb-3"
              placeholder={t("Enter your email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button type="submit" className="btn ez-button-full w-100">
              {t('Send Code')}
            </button>
          </form>
        )}

        {!blockedMessage && step === 2 && (
          <div className="otp-box" onPaste={handlePaste}>

            <p>{t('LoginText')}</p>
            <p>{t('Enter the 6-digit code')}</p>
            <div className="d-flex justify-content-center gap-2 mb-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  ref={(el) => (inputsRef.current[index] = el)}
                  className="otp-input"
                />
              ))}
            </div>

            {/* ✅ Back Button */}
            <button
              type="button"
              className="btn ez-button-light w-100"
              onClick={() => {
                setStep(1);
                setOtp(new Array(6).fill(""));
                clearPendingOtp();
                setTimeout(() => document.querySelector("input[type=email]")?.focus(), 200);
              }}
            >
              {t('Back')}
            </button>
          </div>
        )}

        {blockedMessage && (
          <div className="text-center">
            <h4 className="text-danger mb-3">
              {t("Access denied")}
            </h4>

            <p className="mb-4">
              {blockedMessage}
            </p>

            <button
              className="btn ez-button-full w-100"
              onClick={() => {
                setBlockedMessage("");
                setStep(1);
                setEmail("");
                clearPendingOtp();
              }}
            >
              {t("Back to login")}
            </button>
          </div>
        )}
        <Notification
          message={errorMessage}
          type="danger"
          onClose={() => setErrorMessage("")}
        />
      </div>
    </div>
  );
}

export default Login;
